import React, { ReactNode, useEffect } from 'react';
import { useAuthUserOrNull } from '@frontegg/react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { isPrevUrlValid } from '@ww/login-ui-components/lib/utils/url';
import { generatePlaygroundCode as GENERATE_PLAYGROUND_CODE } from 'gql/mutations/playground.mutations';
import { PREV_URL_KEY, getAppName, isServerAuthType } from 'utils/search-params';
import { declinedFronteggLogin, rememberFronteggUser } from 'utils/frontegg-user';

interface ServerAuthHandoffProps {
  children: ReactNode;
}

interface GeneratePlaygroundCodeResult {
  generatePlaygroundCode: string;
}

export function ServerAuthHandoff({ children }: ServerAuthHandoffProps): JSX.Element | null {
  const fronteggUser = useAuthUserOrNull();
  const [searchParams] = useSearchParams();
  const accessToken = fronteggUser?.accessToken;
  const previousUrl = searchParams.get(PREV_URL_KEY);

  const appName = getAppName(searchParams);

  const isServerHandoff = isServerAuthType(searchParams) && Boolean(appName);
  const hasFronteggSession = Boolean(accessToken) && !declinedFronteggLogin(searchParams);
  const handoffUrl = isPrevUrlValid(previousUrl) ? previousUrl : null;
  const canHandOffToPrevUrl = isServerHandoff && hasFronteggSession && Boolean(handoffUrl);

  const [generateCode] = useMutation<GeneratePlaygroundCodeResult>(GENERATE_PLAYGROUND_CODE, {
    onCompleted: ({ generatePlaygroundCode: code }) => {
      if (!handoffUrl) {
        return;
      }

      const url = new URL(handoffUrl);
      url.searchParams.set('code', code);
      window.location.replace(url.toString());
    }
  });

  useEffect(() => {
    if (isServerHandoff && hasFronteggSession) {
      rememberFronteggUser();
    }
  }, [isServerHandoff, hasFronteggSession]);

  useEffect(() => {
    if (canHandOffToPrevUrl) {
      generateCode({
        context: { headers: { authorization: `Bearer ${accessToken}` } }
      });
    }
  }, [canHandOffToPrevUrl, previousUrl, accessToken, generateCode]);

  if (canHandOffToPrevUrl) {
    return null;
  }

  return <>{children}</>;
}
